import { useCallback, useEffect, useState } from 'react';
import { apiCall, default as API_CONFIG } from '../config/api';

/**
 * useSecurities -- the curated alias map plus its mutations.
 *
 * A "security identity" is one company and every symbol string it is referred
 * to by. Lanxess is held at Schwab as the ADR LNXSY but Russ posts about it as
 * LXS / LXS.DE, and without an identity linking them those are unrelated
 * records: the holding's detail page 404s on a position Russ discusses weekly.
 *
 * Every mutation returns the saved identity and refreshes the list, because
 * the backend invalidates its resolver cache on write -- so the next read is
 * already consistent and there is nothing to reconcile optimistically.
 */
export const useSecurities = () => {
  const [securities, setSecurities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSecurities(await apiCall(API_CONFIG.endpoints.securities()) || []);
    } catch (err) {
      console.error('Failed to load securities', err);
      setError('Failed to load securities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  /**
   * Create or update an identity's own fields. Aliases are edited separately.
   *
   * `reloadAfter: false` skips the refetch, for callers issuing several writes
   * in a row -- otherwise the list reloads (and flickers) once per write.
   */
  const save = useCallback(async (canonical, fields, { reloadAfter = true } = {}) => {
    const saved = await apiCall(API_CONFIG.endpoints.securityByCanonical(canonical), {
      method: 'PUT',
      body: JSON.stringify(fields || {}),
    });
    if (reloadAfter) await reload();
    return saved;
  }, [reload]);

  const remove = useCallback(async (canonical) => {
    await apiCall(API_CONFIG.endpoints.securityByCanonical(canonical), { method: 'DELETE' });
    await reload();
  }, [reload]);

  /**
   * Link one more string to an identity. Throws with the server's message on
   * a 409 -- the interesting case being "'LXS' already resolves to SOMEOTHER",
   * which the caller should show rather than swallow.
   */
  const addAlias = useCallback(async (
    canonical,
    { value, kind = 'symbol', exchange },
    { reloadAfter = true } = {},
  ) => {
    const saved = await apiCall(API_CONFIG.endpoints.securityAliases(canonical), {
      method: 'POST',
      body: JSON.stringify({ value, kind, exchange }),
    });
    if (reloadAfter) await reload();
    return saved;
  }, [reload]);

  const removeAlias = useCallback(async (canonical, value) => {
    const saved = await apiCall(API_CONFIG.endpoints.securityAlias(canonical, value), {
      method: 'DELETE',
    });
    await reload();
    return saved;
  }, [reload]);

  return { securities, loading, error, reload, save, remove, addAlias, removeAlias };
};

/**
 * useUnmatchedSymbols -- the curation work queue. Every symbol the projections
 * know about that no identity claims, most-recently-seen first. Mostly plain
 * US tickers that need nothing; the ones worth curating are the foreign
 * listings sitting next to a holding you recognise.
 */
export const useUnmatchedSymbols = () => {
  const [unmatched, setUnmatched] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUnmatched(await apiCall(API_CONFIG.endpoints.securitiesUnmatched()) || []);
    } catch (err) {
      console.error('Failed to load unmatched symbols', err);
      setError('Failed to load unmatched symbols');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return { unmatched, loading, error, reload };
};
